/**
 * Strings for the AI Support assistant.
 *
 * The app has no i18n library and hardcodes Hebrew inline elsewhere; this is a
 * scoped layer for this feature rather than a retrofit of the whole app.
 * Hebrew is the default — English is the alternative, not the base.
 */

export type AISupportLang = "he" | "en";

export const AI_SUPPORT_LANG_KEY = "aiSupportLang";

const strings = {
  he: {
    title: "תמיכה חכמה",
    launcherLabel: "פתיחת תמיכה חכמה",
    close: "סגירה",
    walkthroughStartHere: "המדריך מתחיל במסך אחר",
    walkthroughOpenFirst: "כדי לעקוב אחרי ההסבר, פתחי קודם את",
    walkthroughTakeMeThere: "קחי אותי לשם",
    minimize: "מזעור",
    placeholder: "במה אפשר לעזור?",
    send: "שליחה",
    thinking: "חושב…",
    working: "בודק…",
    firstUseNotice:
      "העוזר רואה את המסך הנוכחי ואת השגיאות האחרונות בדפדפן שלך, כדי שלא תצטרכי לתאר אותן.",
    gotIt: "הבנתי",
    escalate: "העברה לתמיכה אנושית",
    escalated: "הפנייה הועברה לתמיכה אנושית.",
    awaitingApproval: "ממתין לאישור מהתמיכה…",
    // Shown instead of the line above when the therapist administers user
    // accounts: the action is one she performs by hand anyway, so there is
    // nobody to wait for.
    approvalYours: "הפעולה הזו באישורך:",
    approveAction: "אישור",
    denyAction: "לא לאשר",
    approvalFailed: "ההחלטה לא נשלחה. אפשר לנסות שוב.",
    approvalDenied: "התמיכה לא אישרה את הפעולה.",
    approvalApproved: "התמיכה אישרה את הפעולה.",
    error: "משהו השתבש. אפשר לנסות שוב או לפנות לתמיכה אנושית.",
    connectionLost: "החיבור נותק. מנסה להתחבר מחדש…",
    // Shown once, after five quiet minutes, when nobody has finished the
    // conversation. Feminine, like the rest of the Hebrew here.
    //
    // It has to spell out that the buttons are what close the conversation:
    // therapists press the × and reasonably assume that finished it, so the
    // session stays open for days and no feedback is ever given.
    closePrompt: "עדיין כאן? סגירת החלון לא סוגרת את השיחה — היא נשארת פתוחה עד שתלחצי על אחד הכפתורים למטה: \"נפתר\", \"עזר\" או \"לא עזר\". זה לוקח שנייה, ואפשר גם להוסיף הערה במילים שלך — המשוב הזה הוא מה שמשפר את התשובות בפעם הבאה.",
    // Shown when she presses × on a conversation that is still open.
    closeOnDismissTitle: "לפני שנסגור — איך זה נגמר?",
    closeOnDismissBody: "השיחה נשארת פתוחה עד שתבחרי. בחירה כאן סוגרת אותה, ואפשר להוסיף הערה.",
    closeOnDismissSkip: "רק למזער, אחליט אחר כך",
    verdictPrompt: "עזרתי?",
    verdictFixed: "נפתר",
    verdictHelped: "עזר",
    verdictDidntHelp: "לא עזר",
    verdictNeedHuman: "צריך תמיכה אנושית",
    verdictThanks: "תודה על המשוב.",
    feedbackPrompt: "רוצה להוסיף משהו במילים שלך?",
    feedbackHint: "מה שתכתבי כאן נלמד ועוזר לענות טוב יותר בפעם הבאה.",
    feedbackPlaceholder: "מה היה חסר, מה לא היה מדויק, או מה בעצם פתר את זה…",
    feedbackSend: "שליחה",
    feedbackThanks: "תודה — זה ייקרא וישפר את התשובות הבאות.",
    newConversation: "שיחה חדשה",
    langToggle: "EN",
    emptyState: "אפשר לשאול כל שאלה על המערכת — או פשוט לכתוב מה לא עובד.",
    history: "שיחות קודמות",
    filterOpen: "פתוחות",
    filterAll: "הכל",
    openSessions: "יש לך שיחות פתוחות",
    historyEmpty: "אין עדיין שיחות קודמות.",
    historyEmptyOpen: "אין שיחות פתוחות.",
    back: "חזרה",
    fromSupport: "תמיכה אנושית",
    withSupport: "אצל התמיכה",
    newAnswer: "יש תשובה חדשה",
    markSolved: "סמן כנפתר",
    resumeGuide: "המשך הדרכה",
    solved: "השיחה סומנה כנפתרה",
  },
  en: {
    title: "AI Support",
    launcherLabel: "Open AI Support",
    close: "Close",
    walkthroughStartHere: "This guide starts on another screen",
    walkthroughOpenFirst: "To follow it, first open",
    walkthroughTakeMeThere: "Take me there",
    minimize: "Minimize",
    placeholder: "What can I help with?",
    send: "Send",
    thinking: "Thinking…",
    working: "Checking…",
    firstUseNotice:
      "The assistant can see the screen you're on and recent errors from your browser, so you don't have to describe them.",
    gotIt: "Got it",
    escalate: "Hand to human support",
    escalated: "This has been handed to human support.",
    awaitingApproval: "Waiting for support to approve…",
    approvalYours: "This one is yours to approve:",
    approveAction: "Approve",
    denyAction: "Don't approve",
    approvalFailed: "That decision didn't go through. Try again.",
    approvalDenied: "Support declined that action.",
    approvalApproved: "Support approved that action.",
    error: "Something went wrong. Try again, or hand this to human support.",
    connectionLost: "Connection lost. Reconnecting…",
    closePrompt: "Still there? Closing this window does not close the conversation — it stays open until you press one of the buttons below: \"Solved\", \"Helped\" or \"Didn't help\". It takes a second, and you can add a comment in your own words — that feedback is what makes the next answer better.",
    closeOnDismissTitle: "Before you go — how did it end?",
    closeOnDismissBody: "The conversation stays open until you choose. Picking one closes it, and you can add a comment.",
    closeOnDismissSkip: "Just minimise, I'll decide later",
    verdictPrompt: "Did that help?",
    verdictFixed: "Solved",
    verdictHelped: "Helped",
    verdictDidntHelp: "Didn't help",
    verdictNeedHuman: "Need human support",
    verdictThanks: "Thanks for the feedback.",
    feedbackPrompt: "Anything to add, in your own words?",
    feedbackHint: "What you write here is read and used to answer better next time.",
    feedbackPlaceholder: "What was missing, what was wrong, or what actually fixed it…",
    feedbackSend: "Send",
    feedbackThanks: "Thanks — this will be read and will improve the next answers.",
    newConversation: "New chat",
    langToggle: "עב",
    emptyState: "Ask anything about the system — or just say what isn't working.",
    history: "Past conversations",
    filterOpen: "Open",
    filterAll: "All",
    openSessions: "You have open conversations",
    historyEmpty: "No past conversations yet.",
    historyEmptyOpen: "No open conversations.",
    back: "Back",
    fromSupport: "Human support",
    withSupport: "with support",
    newAnswer: "New answer",
    markSolved: "Mark solved",
    resumeGuide: "Resume guide",
    solved: "Marked as solved",
  },
} as const;

export type AISupportKey = keyof typeof strings.he;

export const t = (lang: AISupportLang, key: AISupportKey): string =>
  strings[lang]?.[key] ?? strings.he[key];

/** Reading direction for the interface chrome. Message bodies use dir="auto". */
export const dirFor = (lang: AISupportLang): "rtl" | "ltr" => (lang === "he" ? "rtl" : "ltr");

export const readStoredLang = (): AISupportLang => {
  try {
    const stored = localStorage.getItem(AI_SUPPORT_LANG_KEY);
    return stored === "en" ? "en" : "he";
  } catch {
    return "he";
  }
};

export const storeLang = (lang: AISupportLang): void => {
  try {
    localStorage.setItem(AI_SUPPORT_LANG_KEY, lang);
  } catch {
    /* private browsing — the toggle just won't persist */
  }
};
